import React, { useState, useEffect, useRef, useMemo } from "react";

/* ═══════════════════════════════════════════════════════════════════════
   GRANTHA — THE LIVING DOCUMENT FABRIC
   Radial constellation of documents orbiting five semantic anchors
   Pure SVG + CSS keyframes + React state
   ═══════════════════════════════════════════════════════════════════════ */

const ANCHORS = [
  { id: "pbs",      x: 180, y: 140, shape: "diamond", label: "PBS · UNIT-02",      count: 4, hueVar: 400 },
  { id: "tag",      x: 460, y: 150, shape: "hexagon", label: "TAG · P-2401A",      count: 5, hueVar: 500 },
  { id: "workflow", x: 320, y: 280, shape: "octagon", label: "WORKFLOW · REVIEW",  count: 6, hueVar: 600 },
  { id: "reviewer", x: 150, y: 420, shape: "circle",  label: "REVIEWER · LEAD",    count: 3, hueVar: 300 },
  { id: "external", x: 490, y: 430, shape: "extrect", label: "EXTERNAL · VENDOR",  count: 3, hueVar: 700 },
];

const ANCHOR_LINKS = [
  ["pbs", "tag"],
  ["tag", "workflow"],
  ["workflow", "reviewer"],
  ["workflow", "external"],
  ["pbs", "workflow"],
];

const DOC_IDS = [
  "DS-1001","DS-1042","DS-2301","DS-3418","DS-4102",
  "PF-0102","PF-0203","PF-0301","PF-1001",
  "PI-4208","PI-4301","PI-4412","PI-5102","PI-5203",
  "SP-1201","SP-2301","SP-3401","SP-4501",
  "DW-0812","DW-1104","DW-2408","DW-3501","DW-4201",
  "RP-ENG-021","RP-HSE-087","RP-QA-142",
  "CL-P-012","CL-H-034","CL-S-078",
  "MN-OPS-001","MN-MNT-015",
];
const REVS = ["rev A1","rev A2","rev B1","rev B2","rev 01","rev 02","rev 03","rev IFD","rev IFR","rev IFC"];
const STATES = ["draft","draft","draft","review","approved","issued","obsolete"];

const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];
const uuid = () => Math.random().toString(36).slice(2, 9);
const docTypeFromId = (id) => id.slice(0, 2);

/* ─── generate documents with random orbits per anchor ─── */
function generateInitialDocs() {
  const docs = [];
  ANCHORS.forEach((a) => {
    for (let i = 0; i < a.count; i++) {
      const radius = 50 + Math.random() * 35;
      const angleOffset = (i / a.count) * 360 + Math.random() * 30;
      const period = 30 + Math.random() * 20; // 30–50s
      const direction = Math.random() < 0.5 ? 1 : -1;
      docs.push({
        id: uuid(),
        anchorId: a.id,
        docId: rand(DOC_IDS),
        rev: rand(REVS),
        state: rand(STATES),
        radius,
        angleOffset,
        period,
        direction,
      });
    }
  });
  return docs;
}

/* ─── Cross-reference pairs for dotted lines ─── */
function generateCrossRefs(docs) {
  const refs = [];
  for (let i = 0; i < 6; i++) {
    const a = docs[Math.floor(Math.random() * docs.length)];
    const b = docs[Math.floor(Math.random() * docs.length)];
    if (a.id !== b.id) refs.push({ from: a.id, to: b.id });
  }
  return refs;
}

/* ═══════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════ */
export default function GranthaDocumentFabric() {
  const [docs, setDocs] = useState(generateInitialDocs);
  const [markups, setMarkups] = useState([]); // { id, docId, type }
  const [sparks, setSparks] = useState([]);   // { id, docId, label }
  const [transmittals, setTransmittals] = useState([]); // { id, fromDocId }
  const [stats, setStats] = useState({ docs: 12847, tags: 847, reviewers: 47, workflows: 12 });
  const [reduceMotion, setReduceMotion] = useState(false);

  const crossRefs = useMemo(() => generateCrossRefs(docs), [docs]);

  /* reduced motion */
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const fn = (e) => setReduceMotion(e.matches);
    mq.addEventListener("change", fn);
    return () => mq.removeEventListener("change", fn);
  }, []);

  /* Event 1 — markup bubbles every 5-7s */
  useEffect(() => {
    if (reduceMotion) return;
    const spawn = () => {
      const doc = docs[Math.floor(Math.random() * docs.length)];
      const id = uuid();
      setMarkups((m) => [...m, { id, docId: doc.id }]);
      setTimeout(() => setMarkups((m) => m.filter((x) => x.id !== id)), 2800);
    };
    const itv = setInterval(spawn, 5500 + Math.random() * 1500);
    return () => clearInterval(itv);
  }, [docs, reduceMotion]);

  /* Event 3 — revision crystallization every 10-14s (orange spark + rev update) */
  useEffect(() => {
    if (reduceMotion) return;
    const fire = () => {
      const idx = Math.floor(Math.random() * docs.length);
      const doc = docs[idx];
      const id = uuid();
      setSparks((s) => [...s, { id, docId: doc.id, label: "rev ↑", orange: true }]);
      setTimeout(() => setSparks((s) => s.filter((x) => x.id !== id)), 1200);
      // bump revision
      setDocs((curr) => curr.map((d, i) => (i === idx ? { ...d, rev: rand(REVS), state: "issued" } : d)));
    };
    const itv = setInterval(fire, 11000 + Math.random() * 3000);
    return () => clearInterval(itv);
  }, [docs, reduceMotion]);

  /* Event 4 — transmittal departure every 12-16s */
  useEffect(() => {
    if (reduceMotion) return;
    const fire = () => {
      const doc = docs[Math.floor(Math.random() * docs.length)];
      const id = uuid();
      setTransmittals((t) => [...t, { id, fromAnchor: doc.anchorId }]);
      setTimeout(() => setTransmittals((t) => t.filter((x) => x.id !== id)), 2500);
    };
    const itv = setInterval(fire, 13500 + Math.random() * 2500);
    return () => clearInterval(itv);
  }, [docs, reduceMotion]);

  /* Event 5 — new document crystallization every 18-22s */
  useEffect(() => {
    if (reduceMotion) return;
    const fire = () => {
      const anchor = ANCHORS[Math.floor(Math.random() * ANCHORS.length)];
      const newDoc = {
        id: uuid(),
        anchorId: anchor.id,
        docId: rand(DOC_IDS),
        rev: rand(REVS),
        state: "draft",
        radius: 50 + Math.random() * 30,
        angleOffset: Math.random() * 360,
        period: 30 + Math.random() * 20,
        direction: Math.random() < 0.5 ? 1 : -1,
        justBorn: true,
      };
      const id = uuid();
      setSparks((s) => [...s, { id, docId: newDoc.id, label: "+ new", orange: true }]);
      setTimeout(() => setSparks((s) => s.filter((x) => x.id !== id)), 1000);
      setDocs((curr) => {
        const next = [...curr, newDoc];
        if (next.length > 24) next.shift();
        return next;
      });
    };
    const itv = setInterval(fire, 19000 + Math.random() * 4000);
    return () => clearInterval(itv);
  }, [reduceMotion]);

  /* stats ticker */
  useEffect(() => {
    if (reduceMotion) return;
    const itv = setInterval(() => {
      setStats((s) => ({
        docs: s.docs + (Math.random() < 0.5 ? 1 : -1),
        tags: s.tags + (Math.random() < 0.3 ? 1 : 0),
        reviewers: 42 + Math.floor(Math.random() * 10),
        workflows: s.workflows,
      }));
    }, 2500);
    return () => clearInterval(itv);
  }, [reduceMotion]);

  /* ─────── RENDER ─────── */
  const anchorById = useMemo(() => Object.fromEntries(ANCHORS.map((a) => [a.id, a])), []);
  const docById = useMemo(() => Object.fromEntries(docs.map((d) => [d.id, d])), [docs]);

  return (
    <div className="gdf-wrap" data-testid="gdf-canvas" role="img"
         aria-label="Animated illustration: Docuweave as a living document fabric. Documents orbit around five semantic anchors — a Product Breakdown Structure node, a Tag cluster, a Workflow anchor at the center, a Reviewer node, and an External Party. Documents are continuously marked up, reviewed, revised, and transmitted. Connection lines show semantic relationships between anchors and across documents.">
      <svg viewBox="0 0 640 560" className={`gdf-svg ${reduceMotion ? "gdf-reduced" : ""}`}
           preserveAspectRatio="xMidYMid meet" aria-hidden="true">
        <defs>
          <radialGradient id="gdf-anchor-glow">
            <stop offset="0%" stopColor="#5790C9" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#5790C9" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="gdf-workflow-glow">
            <stop offset="0%" stopColor="#5790C9" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#5790C9" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* ═══════ TOP OVERLAY ═══════ */}
        <rect x="0" y="0" width="640" height="24" className="gdf-topbar-bg" />
        <text x="8" y="16" className="gdf-top-txt">PROJECT · {stats.docs.toLocaleString()} DOCS · {stats.tags} TAGS</text>
        <text x="320" y="16" textAnchor="middle" className="gdf-top-txt">LIVE · {stats.reviewers} REVIEWERS · {stats.workflows} WORKFLOWS</text>
        <g transform="translate(568, 12)">
          <path d="M0,-5 L-3,0 L0,0 L-1,4 L4,-2 L1,-2 Z" className="gdf-spark-orange" />
          <text x="8" y="4" className="gdf-top-txt">AI-NATIVE</text>
        </g>
        <line x1="0" y1="25" x2="640" y2="25" className="gdf-hair" />

        {/* ═══════ ANCHOR-TO-ANCHOR CONNECTIONS ═══════ */}
        <g className="gdf-anchor-links">
          {ANCHOR_LINKS.map(([aId, bId]) => {
            const a = anchorById[aId], b = anchorById[bId];
            const cx = (a.x + b.x) / 2;
            const cy = (a.y + b.y) / 2 - 20;
            return (
              <path key={`${aId}-${bId}`}
                    d={`M${a.x},${a.y} Q${cx},${cy} ${b.x},${b.y}`}
                    className="gdf-anchor-link" />
            );
          })}
        </g>

        {/* ═══════ CROSS-REFERENCE DOTTED LINES (doc ↔ doc) ═══════ */}
        {/* Since docs orbit, static cross-refs are approximated between their anchors */}
        <g className="gdf-xrefs">
          {crossRefs.map((r, i) => {
            const a = docById[r.from], b = docById[r.to];
            if (!a || !b) return null;
            const anA = anchorById[a.anchorId], anB = anchorById[b.anchorId];
            return (
              <line key={i}
                    x1={anA.x} y1={anA.y}
                    x2={anB.x} y2={anB.y}
                    className="gdf-xref" />
            );
          })}
        </g>

        {/* ═══════ ANCHORS ═══════ */}
        {ANCHORS.map((a) => (
          <AnchorNode key={a.id} anchor={a} hasTransmittalIncoming={
            a.id === "external" && transmittals.some((t) => anchorById[t.fromAnchor]?.id !== "external")
          } />
        ))}

        {/* ═══════ DOCUMENT NODES with orbit wrappers ═══════ */}
        {docs.map((d) => {
          const anchor = anchorById[d.anchorId];
          return (
            <DocOrbit
              key={d.id}
              doc={d}
              anchor={anchor}
              hasMarkup={markups.some((m) => m.docId === d.id)}
              spark={sparks.find((s) => s.docId === d.id)}
            />
          );
        })}

        {/* ═══════ TRANSMITTAL ARROWS ═══════ */}
        <g className="gdf-transmittals">
          {transmittals.map((t) => {
            const from = anchorById[t.fromAnchor];
            const to = anchorById["external"];
            if (!from || from.id === "external") return null;
            return (
              <g key={t.id} className="gdf-transmittal">
                <circle r="3" className="gdf-trans-pkt">
                  <animateMotion dur="2s" repeatCount="1" fill="freeze"
                                 path={`M${from.x},${from.y} Q${(from.x + to.x) / 2},${(from.y + to.y) / 2 - 20} ${to.x},${to.y}`} />
                </circle>
                <text x={from.x + 6} y={from.y - 10} className="gdf-trans-label">→ TRANSMITTED</text>
              </g>
            );
          })}
        </g>

        {/* ═══════ BOTTOM LABELS ═══════ */}
        <line x1="0" y1="540" x2="640" y2="540" className="gdf-hair" />
        <text x="8" y="554" className="gdf-bot-label">CONNECTED</text>
        <text x="320" y="554" textAnchor="middle" className="gdf-bot-label gdf-bot-label-accent">VERSIONED</text>
        <text x="632" y="554" textAnchor="end" className="gdf-bot-label gdf-bot-label-accent">ALIVE</text>
      </svg>

      <span className="sr-only">
        Docuweave maintains a living fabric of documents connected through semantic anchors:
        Product Breakdown Structure, Tags, Workflows, Reviewers, and External Parties.
        Documents orbit these anchors with continuous markups, reviews, revisions, and transmittals.
      </span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   ANCHOR NODE
   ═══════════════════════════════════════════════════════════════════════ */
function AnchorNode({ anchor }) {
  const { x, y, shape, label, id } = anchor;
  const isCenter = id === "workflow";
  const isExternal = id === "external";
  return (
    <g className={`gdf-anchor gdf-anchor-${id} ${isCenter ? "gdf-anchor-center" : ""}`}
       transform={`translate(${x}, ${y})`}>
      {/* outer glow pulse */}
      <circle r={isCenter ? 28 : 22} className="gdf-anchor-halo" fill={isCenter ? "url(#gdf-workflow-glow)" : "url(#gdf-anchor-glow)"} />
      {/* shape */}
      {shape === "diamond" && (
        <polygon points="-16,0 0,-16 16,0 0,16" className="gdf-anchor-shape" />
      )}
      {shape === "hexagon" && (
        <polygon points="-14,-8 -14,8 0,16 14,8 14,-8 0,-16" className="gdf-anchor-shape" />
      )}
      {shape === "octagon" && (
        <polygon points="-17,-7 -7,-17 7,-17 17,-7 17,7 7,17 -7,17 -17,7" className="gdf-anchor-shape gdf-anchor-workflow-shape" />
      )}
      {shape === "circle" && (
        <circle r="15" className="gdf-anchor-shape" />
      )}
      {shape === "extrect" && (
        <rect x="-16" y="-14" width="32" height="28" rx="4" className="gdf-anchor-shape gdf-anchor-external-shape" />
      )}
      {/* inner icon symbol */}
      <text y="3" textAnchor="middle" className="gdf-anchor-icon">
        {id === "pbs" ? "P" : id === "tag" ? "#" : id === "workflow" ? "◈" : id === "reviewer" ? "R" : "✈"}
      </text>
      {/* label below */}
      <text y={isCenter ? 36 : 30} textAnchor="middle" className="gdf-anchor-label">{label}</text>
    </g>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   DOCUMENT ORBIT — wrapper that rotates, with counter-rotation on content
   ═══════════════════════════════════════════════════════════════════════ */
function DocOrbit({ doc, anchor, hasMarkup, spark }) {
  const orbitStyle = {
    animationDuration: `${doc.period}s`,
    animationDirection: doc.direction < 0 ? "reverse" : "normal",
    animationDelay: `-${(doc.angleOffset / 360) * doc.period}s`,
    transformOrigin: `${anchor.x}px ${anchor.y}px`,
  };
  const counterStyle = {
    animationDuration: `${doc.period}s`,
    animationDirection: doc.direction < 0 ? "normal" : "reverse",
    animationDelay: `-${(doc.angleOffset / 360) * doc.period}s`,
    transformOrigin: "center",
    transformBox: "fill-box",
  };
  return (
    <g className="gdf-orbit" style={orbitStyle}>
      {/* membership connection line (anchor → document position) */}
      <line x1={anchor.x} y1={anchor.y}
            x2={anchor.x + doc.radius} y2={anchor.y}
            className="gdf-doc-link" />
      {/* document positioned at radius offset */}
      <g transform={`translate(${anchor.x + doc.radius}, ${anchor.y})`}>
        <g className="gdf-doc-counter" style={counterStyle}>
          <DocNode doc={doc} hasMarkup={hasMarkup} spark={spark} />
        </g>
      </g>
    </g>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   DOCUMENT NODE
   ═══════════════════════════════════════════════════════════════════════ */
function DocNode({ doc, hasMarkup, spark }) {
  const stateClass = `gdf-doc-${doc.state}`;
  return (
    <g className={`gdf-doc ${stateClass} ${doc.justBorn ? "gdf-doc-born" : ""}`}>
      {/* main body (with folded corner) */}
      <path d="M-22,-15 L18,-15 L22,-11 L22,15 L-22,15 Z" className="gdf-doc-bg" />
      {/* folded corner */}
      <path d="M18,-15 L22,-11 L18,-11 Z" className="gdf-doc-fold" />
      {/* type code (top-left) */}
      <text x="-19" y="-5" className="gdf-doc-type">{docTypeFromId(doc.docId)}</text>
      {/* doc id (center) */}
      <text x="0" y="3" textAnchor="middle" className="gdf-doc-id">{doc.docId}</text>
      {/* revision (bottom) */}
      <text x="0" y="12" textAnchor="middle" className="gdf-doc-rev">{doc.rev}</text>
      {/* markup bubble */}
      {hasMarkup && (
        <g className="gdf-markup" transform="translate(20, -15)">
          <circle r="5" className="gdf-markup-bubble" />
          <text y="2" textAnchor="middle" className="gdf-markup-glyph">¶</text>
        </g>
      )}
      {/* orange spark */}
      {spark && spark.orange && (
        <g className="gdf-doc-spark" transform="translate(-20, -15)">
          <path d="M0,-5 L-3,0 L0,0 L-1,4 L4,-2 L1,-2 Z" className="gdf-spark-orange" />
          <text x="5" y="4" className="gdf-doc-spark-label">{spark.label}</text>
        </g>
      )}
    </g>
  );
}
